import { raggruppa, riepilogo, type Riga } from "./report";
import { oggettoGara, type Gara, type Lotto } from "./gare";
import type { VoceChecklist } from "./checklist";

/**
 * File Excel del report: foglio "Estrazione" (una riga per gara o lotto) e foglio
 * "Riepilogo" (filtri usati, totali, raggruppamenti). ExcelJS si carica solo qui.
 */
export async function creaExcel(righe: Riga[], descrizioneFiltri: string[]): Promise<Blob> {
  const { default: ExcelJS } = await import("exceljs");
  const wb = new ExcelJS.Workbook();
  wb.creator = "App Gare";
  wb.created = new Date();

  const euro = '#,##0.00 "€"';
  const data = "dd/mm/yyyy hh:mm";

  /* ------------------------------ Estrazione ------------------------------ */
  const ws = wb.addWorksheet("Estrazione", { views: [{ state: "frozen", ySplit: 1 }] });
  ws.columns = [
    { header: "Codice", key: "codice", width: 30 },
    { header: "Lotto", key: "lotto", width: 7 },
    { header: "Oggetto", key: "oggetto", width: 60 },
    { header: "Stazione appaltante", key: "stazione", width: 32 },
    { header: "Tipo di ente", key: "tipoEnte", width: 22 },
    { header: "Sigla", key: "sigla", width: 8 },
    { header: "Servizio", key: "servizio", width: 32 },
    { header: "Scadenza", key: "scadenza", width: 17, style: { numFmt: data } },
    { header: "Importo a base d'asta", key: "importo", width: 18, style: { numFmt: euro } },
    { header: "Valutazione", key: "valutazione", width: 14 },
    { header: "Esito", key: "esito", width: 16 },
    { header: "Importo aggiudicato", key: "aggiudicato", width: 18, style: { numFmt: euro } },
    { header: "Cartella", key: "cartella", width: 14 },
  ];
  for (const r of righe) {
    const row = ws.addRow({
      codice: r.codice,
      lotto: r.lottoNumero ?? null,
      oggetto: r.oggetto,
      stazione: r.stazione,
      tipoEnte: r.tipoEnte ?? "",
      sigla: r.servizio?.fields.Sigla ?? "",
      servizio: r.servizio?.fields.Title ?? "",
      scadenza: r.scadenza ? new Date(r.scadenza) : null,
      importo: r.importo ?? null,
      valutazione: r.valutazione,
      esito: r.esito ?? "",
      aggiudicato: r.importoAggiudicato ?? null,
      cartella: r.cartellaUrl ? { text: "Apri", hyperlink: r.cartellaUrl } : "",
    });
    row.alignment = { vertical: "top", wrapText: true };
  }
  ws.getRow(1).font = { bold: true };
  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: ws.columns.length } };

  /* ------------------------------ Riepilogo ------------------------------ */
  const rs = wb.addWorksheet("Riepilogo");
  rs.columns = [{ width: 42 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 14 }, { width: 18 }, { width: 18 }];
  rs.addRow(["Report gare"]).font = { bold: true, size: 14 };
  rs.addRow([`Creato il ${new Date().toLocaleString("it-IT")}`]);
  for (const d of descrizioneFiltri) rs.addRow([d]);
  rs.addRow([]);

  const t = riepilogo(righe);
  const totali: Array<[string, number | string, string?]> = [
    ["Gare", t.gare],
    ["… a cui si partecipa", t.partecipate],
    ["… a cui non si partecipa", t.nonPartecipate],
    ["… da valutare", t.daValutare],
    ["Partecipazioni (gare senza lotti + lotti)", t.partecipazioni],
    ["Aggiudicate", t.aggiudicate],
    ["Non aggiudicate", t.nonAggiudicate],
    ["In attesa di esito", t.inAttesa],
    ["Tasso di aggiudicazione", t.tasso ?? "—", "0.0%"],
    ["Importo a base d'asta delle partecipazioni", t.importoPartecipato, euro],
    ["Importo aggiudicato", t.importoAggiudicato, euro],
  ];
  for (const [k, v, fmt] of totali) {
    const row = rs.addRow([k, v]);
    if (fmt) row.getCell(2).numFmt = fmt;
  }

  const intestazione = ["Partecipazioni", "Aggiudicate", "Non aggiudicate", "In attesa", "Tasso", "Base d'asta", "Aggiudicato"];
  for (const [titolo, per] of [
    ["Per servizio", "servizio"],
    ["Per tipo di ente", "tipoEnte"],
    ["Per anno di scadenza", "anno"],
  ] as const) {
    rs.addRow([]);
    const h = rs.addRow([titolo, ...intestazione]);
    h.font = { bold: true };
    for (const g of raggruppa(righe, per)) {
      const x = g.riepilogo;
      const row = rs.addRow([g.chiave, x.partecipazioni, x.aggiudicate, x.nonAggiudicate, x.inAttesa, x.tasso ?? "—", x.importoPartecipato, x.importoAggiudicato]);
      row.getCell(6).numFmt = "0.0%";
      row.getCell(7).numFmt = euro;
      row.getCell(8).numFmt = euro;
    }
  }

  const buf = await wb.xlsx.writeBuffer();
  return new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}

/**
 * Checklist dei documenti: un foglio per la gara senza lotti, uno per ciascun lotto
 * (come il vecchio Modello_checklist.xlsx, con plico, soggetto, stato e collegamento).
 */
export async function creaExcelChecklist(
  g: Gara,
  fogli: Array<{ lotto?: Lotto; voci: VoceChecklist[] }>,
): Promise<Blob> {
  const { default: ExcelJS } = await import("exceljs");
  const wb = new ExcelJS.Workbook();
  wb.creator = "App Gare";
  wb.created = new Date();
  for (const { lotto, voci } of fogli) {
    const ws = wb.addWorksheet(lotto ? `Lotto ${lotto.fields.Numero}` : "Checklist", { views: [{ state: "frozen", ySplit: 4 }] });
    ws.columns = [{ width: 48 }, { width: 30 }, { width: 28 }, { width: 14 }, { width: 40 }, { width: 40 }];
    ws.addRow([`${g.fields.Codice ?? ""}${lotto ? ` – Lotto ${lotto.fields.Numero}` : ""}`]).font = { bold: true, size: 13 };
    ws.addRow([lotto?.fields.Title ?? oggettoGara(g)]);
    ws.addRow([`${g.fields.Stazione ?? ""} · aggiornata al ${new Date().toLocaleString("it-IT")}`]);
    const h = ws.addRow(["Documento", "Soggetto", "Plico", "Stato", "File", "Note"]);
    h.font = { bold: true };
    for (const v of voci) {
      const f = v.fields;
      const row = ws.addRow([
        f.Title ?? "",
        f.Soggetto ?? "",
        f.Plico ?? "",
        f.Stato ?? "da fare",
        f.FileUrl ? { text: f.FileNome || "Apri", hyperlink: f.FileUrl } : "",
        f.Note ?? "",
      ]);
      row.alignment = { vertical: "top", wrapText: true };
    }
    ws.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4, column: 6 } };
  }
  const buf = await wb.xlsx.writeBuffer();
  return new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}

export function scarica(blob: Blob, nome: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
